import React from "react";
import { Box, Button, Typography } from "@mui/material";
import { motion } from "framer-motion";
import AppleIcon from "@mui/icons-material/Apple";
import PlayArrowIcon from "@mui/icons-material/PlayArrow";
import RestaurantIcon from "@mui/icons-material/Restaurant";
import AccessTimeIcon from "@mui/icons-material/AccessTime";
import LocationOnIcon from "@mui/icons-material/LocationOn";
import CardGiftcardIcon from "@mui/icons-material/CardGiftcard";

const MotionBox = motion(Box);

const benefits = [
  { icon: <AccessTimeIcon />, title: "Fast & easy", detail: "ordering" },
  { icon: <LocationOnIcon />, title: "Real-time", detail: "order tracking" },
  { icon: <CardGiftcardIcon />, title: "Exclusive", detail: "app offers" },
];

function MobileDownloadMainSection() {
  return (
    <Box
      sx={{
        position: "relative",
        overflow: "hidden",
        width: "100%",
        minHeight: { xs: 0, md: 450 },
        display: "grid",
        gridTemplateColumns: { xs: "1fr", md: "1fr 0.9fr" },
        alignItems: "center",
        gap: { xs: 4, md: 3 },
        px: { xs: 2, sm: 4, md: 5, lg: 8 },
        py: { xs: 5, sm: 6, md: 5 },
        // borderRadius: { xs: 3, md: 5 },
        // background: "radial-gradient(ellipse at 77% 52%, #fff8ed 0%, #fffdf7 43%, #f8f6ed 100%)",
      }}
    >
      <MotionBox
        initial={{ opacity: 0, y: 20 }}
        whileInView={{ opacity: 1, y: 0 }}
        viewport={{ once: true, amount: 0.2 }}
        transition={{ duration: 0.55 }}
        sx={{ position: "relative", zIndex: 1, maxWidth: 610, mx: { xs: "auto", md: 0 }, width: "100%" }}
      >
        <Box
          sx={{
            display: "inline-flex",
            alignItems: "center",
            gap: 1,
            mb: { xs: 2, md: 2.5 },
            px: 2,
            py: 0.8,
            borderRadius: 99,
            color: "#C65D38",
            backgroundColor: "#F7E8D8",
          }}
        >
          <RestaurantIcon sx={{ fontSize: 18 }} />
          <Typography sx={{ fontSize: 12, fontWeight: 700, letterSpacing: 1.1, textTransform: "uppercase" }}>
            Our mobile app
          </Typography>
        </Box>

        <Typography
          component="h2"
          sx={{
            color: "#252920",
            fontSize: { xs: "clamp(34px, 8vw, 46px)", md: "clamp(42px, 4vw, 62px)" },
            fontWeight: 700,
            letterSpacing: "-0.045em",
            lineHeight: 1.08,
            maxWidth: 560,
          }}
        >
          Download Our <Box component="span" sx={{ color: "#C65D38" }}>Restaurant</Box> App
        </Typography>

        <Typography
          sx={{
            mt: 2,
            maxWidth: 545,
            color: "#747767",
            fontSize: { xs: 15, sm: 17, md: 19 },
            lineHeight: 1.75,
          }}
        >
          Order your favorite meals, track deliveries, and enjoy exclusive offers directly from our mobile app.
        </Typography>

        <Box sx={{ display: "flex", flexWrap: "wrap", gap: 1.5, mt: { xs: 3, md: 3.25 } }}>
          <Button
            startIcon={<AppleIcon sx={{ fontSize: "30px !important" }} />}
            sx={{
              minWidth: { xs: 155, sm: 190 },
              px: 2.2,
              py: 1.15,
              borderRadius: 2.5,
              justifyContent: "flex-start",
              color: "#fff",
              backgroundColor: "#252920",
              fontSize: 14,
              lineHeight: 1.15,
              textAlign: "left",
              boxShadow: "0 8px 18px #25292020",
              "&:hover": { backgroundColor: "#3c4235", transform: "translateY(-2px)" },
              transition: "transform .2s ease, background-color .2s ease",
              "& .MuiButton-startIcon": { mr: 1.2 },
            }}
          >
            <Box component="span" sx={{ display: "flex", flexDirection: "column", alignItems: "flex-start" }}>
              <Box component="span" sx={{ fontSize: 9, opacity: 0.8 }}>Download on the</Box>
              <Box component="span" sx={{ fontSize: 17, fontWeight: 600 }}>App Store</Box>
            </Box>
          </Button>
          <Button
            startIcon={<PlayArrowIcon sx={{ fontSize: "31px !important", color: "#70c78a" }} />}
            sx={{
              minWidth: { xs: 155, sm: 190 },
              px: 2.2,
              py: 1.15,
              borderRadius: 2.5,
              justifyContent: "flex-start",
              color: "#fff",
              backgroundColor: "#252920",
              fontSize: 14,
              lineHeight: 1.15,
              textAlign: "left",
              boxShadow: "0 8px 18px #25292020",
              "&:hover": { backgroundColor: "#3c4235", transform: "translateY(-2px)" },
              transition: "transform .2s ease, background-color .2s ease",
              "& .MuiButton-startIcon": { mr: 1.2 },
            }}
          >
            <Box component="span" sx={{ display: "flex", flexDirection: "column", alignItems: "flex-start" }}>
              <Box component="span" sx={{ fontSize: 9, opacity: 0.8 }}>GET IT ON</Box>
              <Box component="span" sx={{ fontSize: 17, fontWeight: 600 }}>Google Play</Box>
            </Box>
          </Button>
        </Box>

        <Box
          sx={{
            display: "flex",
            flexWrap: "wrap",
            gap: { xs: 1.5, sm: 0 },
            mt: { xs: 3.5, md: 4.5 },
            maxWidth: 590,
          }}
        >
          {benefits.map((benefit, index) => (
            <Box
              key={benefit.title}
              sx={{
                display: "flex",
                alignItems: "center",
                gap: 1.2,
                pr: { sm: 2.2 },
                pl: { sm: index === 0 ? 0 : 2.2 },
                mr: { sm: 1.2 },
                borderRight: { sm: index < benefits.length - 1 ? "1px solid #e2dfcf" : "none" },
              }}
            >
              <Box sx={{ width: 42, height: 42, flex: "0 0 42px", borderRadius: "50%", display: "grid", placeItems: "center", color: "#C65D38", backgroundColor: "#F7E8D8" }}>
                {React.cloneElement(benefit.icon, { sx: { fontSize: 21 } })}
              </Box>
              <Typography sx={{ color: "#55594d", fontSize: 12, lineHeight: 1.5, whiteSpace: "nowrap" }}>
                {benefit.title}<br />{benefit.detail}
              </Typography>
            </Box>
          ))}
        </Box>
      </MotionBox>

      <Box
        aria-label="Preview of the restaurant ordering app"
        sx={{
          position: "relative",
          minHeight: { xs: 290, sm: 340, md: 400 },
          display: "grid",
          placeItems: "center",
          isolation: "isolate",
        }}
      >
        <Box sx={{ position: "absolute", width: { xs: 230, sm: 290, md: 360 }, aspectRatio: "1", borderRadius: "50%", background: "linear-gradient(145deg, #f7e5ca, #f2d0a8)", opacity: 0.75, zIndex: -1 }} />
        <MotionBox
          initial={{ opacity: 0, x: 25 }}
          whileInView={{ opacity: 1, x: 0 }}
          viewport={{ once: true, amount: 0.2 }}
          animate={{ y: [0, -7, 0] }}
          transition={{ opacity: { duration: 0.65 }, x: { duration: 0.65 }, y: { repeat: Infinity, duration: 4, ease: "easeInOut" } }}
          sx={{
            width: { xs: 176, sm: 196, md: 218 },
            height: { xs: 308, sm: 342, md: 380 },
            p: "8px",
            border: "4px solid #171918",
            borderRadius: { xs: "30px", md: "36px" },
            backgroundColor: "#171918",
            boxShadow: "0 24px 35px #25292032, inset 0 0 0 1px #6b6e68",
            transform: { md: "rotate(-2deg)" },
          }}
        >
          <Box sx={{ height: "100%", position: "relative", overflow: "hidden", borderRadius: { xs: "22px", md: "28px" }, backgroundColor: "#fffdf7", px: 1.1, pt: 2.5 }}>
            <Box sx={{ position: "absolute", top: 5, left: "50%", transform: "translateX(-50%)", width: 58, height: 13, borderRadius: 8, backgroundColor: "#171918", zIndex: 2 }} />
            <Box sx={{ display: "flex", alignItems: "center", justifyContent: "space-between", mb: 1.2, px: 0.4 }}>
              <Typography sx={{ fontWeight: 800, fontSize: 12, color: "#252920" }}>🍴 Food<Box component="span" sx={{ color: "#C65D38" }}>Hub</Box></Typography>
              <Box sx={{ display: "grid", gap: "3px" }}><Box sx={{ width: 13, height: 2, backgroundColor: "#45483f" }} /><Box sx={{ width: 13, height: 2, backgroundColor: "#45483f" }} /><Box sx={{ width: 13, height: 2, backgroundColor: "#45483f" }} /></Box>
            </Box>
            <Box sx={{ p: 1, mb: 1.1, borderRadius: 2, color: "white", background: "linear-gradient(140deg, #384130, #22271e)" }}>
              <Typography sx={{ fontSize: 11, lineHeight: 1.25, fontWeight: 700 }}>Delicious food<br />delivered to you</Typography>
              <Typography sx={{ mt: 0.4, fontSize: 7, opacity: 0.8 }}>Fresh · fast · tasty</Typography>
              <Box sx={{ mt: 0.7, display: "inline-block", px: 1, py: 0.35, borderRadius: 5, backgroundColor: "#C65D38", fontSize: 7 }}>Order now</Box>
              <Box sx={{ float: "right", fontSize: 30, lineHeight: 1.2 }}>🍔</Box>
            </Box>
            <Box sx={{ display: "flex", justifyContent: "space-between", mb: 1.2 }}>
              {["🍔", "🍕", "🥗", "🥤"].map((item, i) => <Box key={item} sx={{ textAlign: "center", fontSize: 7 }}><Box sx={{ width: 30, height: 30, mb: 0.3, display: "grid", placeItems: "center", borderRadius: "50%", backgroundColor: ["#fae8d5", "#f9efc9", "#e6efdf", "#ece7f4"][i], fontSize: 16 }}>{item}</Box>{["Burgers", "Pizza", "Salads", "Drinks"][i]}</Box>)}
            </Box>
            <Typography sx={{ mb: 0.7, fontSize: 9, fontWeight: 700 }}>Popular items</Typography>
            {[{ emoji: "🍔", name: "Classic burger", price: "$6.99" }, { emoji: "🍕", name: "Margherita pizza", price: "$8.99" }].map((item) => (
              <Box key={item.name} sx={{ display: "flex", alignItems: "center", gap: 0.8, mb: 0.7, p: 0.55, borderRadius: 1.5, backgroundColor: "#fff", boxShadow: "0 2px 8px #25292012" }}>
                <Box sx={{ width: 36, height: 34, display: "grid", placeItems: "center", borderRadius: 1, backgroundColor: "#f6e5d1", fontSize: 22 }}>{item.emoji}</Box>
                <Box sx={{ flex: 1 }}><Typography sx={{ fontSize: 7, fontWeight: 700 }}>{item.name}</Typography><Typography sx={{ fontSize: 7, color: "#747767" }}>★ 4.8 · {item.price}</Typography></Box>
                <Box sx={{ px: 0.6, py: 0.2, borderRadius: 1, color: "white", backgroundColor: "#C65D38", fontSize: 10 }}>+</Box>
              </Box>
            ))}
            <Box sx={{ position: "absolute", bottom: 7, left: 13, right: 13, display: "flex", justifyContent: "space-between", color: "#C65D38", fontSize: 7 }}><span>⌂ Home</span><span>▤ Orders</span><span>☆ Offers</span><span>◯ Profile</span></Box>
          </Box>
        </MotionBox>
      </Box>
    </Box>
  );
}

export default MobileDownloadMainSection;
